import { ReebeloStoreT } from '@lambda/reebelo';
import {
  CaptureMethod,
  CheckoutStatus,
  PaymentProcessor,
  LineItemType,
} from './enum';
import { CustomerAddress } from '../customer/types';
import { PriceComponentsT } from '../catalog-v2/types';

export type CreateCheckoutPaymentT = {
  processor: PaymentProcessor;
  customerId?: string;
  email?: string;
  shippingAddress?: CustomerAddress;
  billingAddress?: CustomerAddress;
  captureMethod?: CaptureMethod;
};

export type CaptureCheckoutPaymentT = {
  captureMethod?: CaptureMethod;
  customerId?: string;
  email?: string;
  shippingAddress?: CustomerAddress;
  billingAddress?: CustomerAddress;
  notes?: CheckoutNote[];
  processor: PaymentProcessor;
};

export type CreateCheckoutT = {
  store: ReebeloStoreT;
  cartId: string;
  sessionId?: string;
  ipAddress?: string;
  userAgent?: string;
  customerId?: string;
  anonymousId?: string;
};

export type CalculateCheckoutTaxT = {
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

export type CheckoutNote = { name: string; value: string };

export enum CheckoutLineItemAction {
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
}

export type UpdateCheckoutLineT = {
  id: string;
  quantity?: number;
  action: CheckoutLineItemAction;
};

export type UpdateCheckoutT = {
  customerId?: string;
  email?: string;
  shippingAddress?: CustomerAddress;
  billingAddress?: CustomerAddress;
  notes?: CheckoutNote[];
  lineItems?: UpdateCheckoutLineT[];
};

export enum DiscountType {
  VOUCHER = 'voucher',
  PROMOTION_TIER = 'promotion_tier',
}

export type CheckoutDiscountT = {
  code: string;
  source: string;
  amount: number;
  applicable: boolean;
  type?: DiscountType;
  name?: string;
};

export type ApplyCheckoutDiscountT = {
  discountCode: string;
};

export type CheckoutAmountT = {
  currency: string;
  subTotal: number;
  taxTotal: number;
  dutiesTotal: number;
  shippingTotal: number;
  discountTotal: number;
  grandTotal: number;
  taxesIncluded?: boolean;
};

export type CheckoutLineInventoryT = {
  onHand: number;
  available: number;
  reserved: number;
};

export type CheckoutLineT = {
  id: string;
  offerId?: string;
  type: LineItemType;
  vendorId?: string;
  vendor?: string;
  variantId?: string;
  typeId?: string;
  shopifyVariantId?: string;
  sku?: string;
  psku?: string;
  price?: number;
  compareAtPrice?: number;
  requiresShipping?: boolean;
  taxable?: boolean;
  taxCalculationId?: string;
  taxConfirmationId?: string;
  title?: string;
  discounts?: CheckoutDiscountT[];
  quantity: number;
  subTotal?: number;
  taxTotal: number;
  dutiesTotal: number;
  shippingTotal: number;
  discountTotal: number;
  grandTotal?: number;
  image?: string;
  notes?: CheckoutNote[];
  categoryPk?: string;
  categoryName?: string;
  origin?: string;
  inventory?: CheckoutLineInventoryT;
  priceComponents?: PriceComponentsT[];
};

export type CheckoutT = {
  id: string;
  customerId?: string;
  sessionId?: string;
  orderId?: string;
  shopifyOrderId?: string;
  email?: string;
  status: CheckoutStatus;
  currency: string;
  subTotal: number;
  taxTotal: number;
  discountTotal: number;
  dutiesTotal: number;
  grandTotal: number;
  taxesIncluded?: boolean;
  shippingAddress?: CustomerAddress;
  billingAddress?: CustomerAddress;
  shopifyStore: ReebeloStoreT;
  shopifyCartId: string;
  shopifyCheckoutId: string;
  paymentIntentId?: string;
  paypalOrderId?: string;
  updatedAt: Date;
  createdAt: Date;
  discounts: CheckoutDiscountT[];
  notes?: CheckoutNote[];
  paymentProcessor: PaymentProcessor;
  lineItems: CheckoutLineT[];
  priceComponents?: PriceComponentsT[];
};

export type CheckoutLineWithRCareSpecs = CheckoutLineT & {
  rCareSpecs?: string;
  rCarePrice?: number;
  categoryName?: string;
  type: LineItemType;
  image?: string;
};

export type CheckoutPaymentT = CheckoutT & {
  clientSecret?: string;
};

// Copied from MsCheckout
type TaxLineT = {
  title: string;
  rate: number;
  price: number;
};

enum DiscountSource {
  SHOPIFY = 'shopify',
  VOUCHERIFY = 'voucherify',
}

type DiscountT = {
  source: DiscountSource;
  code: string;
  amount: number;
  externalId?: string;
};

type NoteT = {
  name: string;
  value: string;
};

type OrderLineItemT = {
  type: LineItemType;
  id: string;
  vendorId: string;
  quantity: number;
  requiresShipping: boolean;
  price: number;
  taxable: boolean;
  title: string;
  notes: NoteT[];
  taxLines: TaxLineT[];
  dutiesLines: TaxLineT[];
  discounts: DiscountT[];
  subTotal: number;
  taxTotal: number;
  dutiesTotal: number;
  shippingTotal: number;
  discountTotal: number;
  grandTotal: number;
  offerId: string;
  variantId: string;
  shopifyVariantId: string;
  priceComponents?: PriceComponentsT[];
};

export enum OrderStatuses {
  OPEN = 'open',
  CLOSED = 'closed',
  CANCELLED = 'cancelled',
  PROCESSING = 'processing',
  FAILED = 'failed',
}

export type CustomerOrderT = {
  id: string;
  status: OrderStatuses;
  orderName?: string;
  shopifyStore: string;
  shopifyId: string;
  shopifyName: string;
  shopifyEnabled?: boolean;
  checkoutId: string;
  customerId: string;
  anonymousId: string;
  sessionId: string;
  ipAddress: string;
  userAgent: string;
  email: string;
  shippingAddress: CustomerAddress;
  billingAddress: CustomerAddress;
  discounts: DiscountT[];
  lineItems: OrderLineItemT[];
  taxesIncluded: boolean;
  taxLines: TaxLineT[];
  notes: NoteT[];
  currency: string;
  subTotal: number;
  taxTotal: number;
  dutiesTotal: number;
  shippingTotal: number;
  discountTotal: number;
  grandTotal: number;
  createdAt: string;
  updatedAt: string;
  priceComponents?: PriceComponentsT[];
};

export enum CheckoutPaymentMethods {
  LINK = 'link',
  CARD = 'card',
  US_BANK_ACCOUNT = 'us_bank_account',
  APPLE_PAY = 'apple_pay',
  GOOGLE_PAY = 'google_pay',
  KLARNA = 'klarna',
  AFFIRM = 'affirm',
  AFTERPAY_CLEARPAY = 'afterpay_clearpay',
  ZIP = 'zip',
  GRABPAY = 'grabpay',
  PAYNOW = 'paynow',
  STRIPE_CUSTOM_PAYMENT = 'stripe_custom_payment',
  PAYPAL = 'paypal',
}
